import axios from "axios";
import {environment} from "~/env";
import {queryHttp} from "~/query/queryHttp";

/** Best human-readable message in an error body: DRF `detail`, a tool `error`, or a JSON-RPC `error.message`. */
export function mcpErrorMessage(data: unknown): string | undefined {
    if (data == null || typeof data !== "object") return undefined;
    const body = data as Record<string, unknown>;
    if (typeof body.detail === "string") return body.detail;
    if (typeof body.error === "string") return body.error;
    const nested = (body.error as {message?: unknown} | null | undefined)?.message;
    return typeof nested === "string" ? nested : undefined;
}

/**
 * Stateless JSON-RPC `tools/call` against a Mithra internal MCP.
 * The endpoint is on the material host, not under `/api`. `queryHttp` still attaches the user JWT.
 */
export async function callMithraMcp(
    mcp: string,
    name: string,
    args: Record<string, unknown>,
): Promise<Record<string, unknown>> {
    const host = environment.materializedApiUrl.replace(/\/api\/?$/, "");
    let body: {result?: unknown} & Record<string, unknown> | undefined;
    try {
        const response = await queryHttp.post(`${host}/cat_mcp/${mcp}/`, {
            jsonrpc: "2.0",
            id: 1,
            method: "tools/call",
            params: {name, arguments: args},
        });
        body = response.data;
    } catch (error) {
        if (!axios.isAxiosError(error)) throw error;
        throw new Error(
            mcpErrorMessage(error.response?.data)
            ?? (error.response ? "The request failed." : "Could not reach the server."),
        );
    }
    const result = body?.result as {isError?: boolean; content?: {text?: string}[]} | undefined;
    if (result == null) {
        throw new Error(mcpErrorMessage(body) ?? "Unexpected response from the server.");
    }
    const text = result.content?.[0]?.text;
    let data: Record<string, unknown> = {};
    if (typeof text === "string" && text) {
        let parsed: unknown;
        try {
            parsed = JSON.parse(text);
        } catch {
            throw new Error("Unexpected response from the server.");
        }
        if (parsed != null && typeof parsed === "object" && !Array.isArray(parsed)) {
            data = parsed as Record<string, unknown>;
        }
    }
    if (result.isError) {
        throw new Error(typeof data.error === "string" ? data.error : "The request failed.");
    }
    return data;
}
